import { useQueries, useQuery } from '@tanstack/react-query';

import {
  getBacklog,
  getProjectSprints,
  getSprintReport,
  getSprintWorkItems,
  getSprintWorkload,
} from '../api/sprint';
import { sprintsQueryKey } from './useSprintMutations';
import { Task } from 'types/project/tasks';

/**
 * Sits under the 'tasks' prefix on purpose: every work item create, update,
 * delete and reorder already invalidates ['tasks'], so the backlog refreshes
 * along with the other task lists.
 */
export const backlogQueryKey = (projectUuid: string) =>
  ['tasks', 'backlog', projectUuid] as const;

export const useProjectSprintsQuery = (projectUuid?: string) =>
  useQuery({
    queryKey: sprintsQueryKey(projectUuid ?? ''),
    queryFn: () => getProjectSprints(projectUuid as string),
    enabled: !!projectUuid,
  });

/** Under the project's sprints key, so completing a sprint refreshes it too. */
export const useCompletedSprintsQuery = (projectUuid?: string) =>
  useQuery({
    queryKey: [...sprintsQueryKey(projectUuid ?? ''), 'COMPLETED'] as const,
    queryFn: () => getProjectSprints(projectUuid as string, ['COMPLETED']),
    enabled: !!projectUuid,
  });

export const useBacklogQuery = (projectUuid?: string) =>
  useQuery({
    queryKey: backlogQueryKey(projectUuid ?? ''),
    queryFn: () => getBacklog(projectUuid as string),
    enabled: !!projectUuid,
  });

/** Under the 'tasks' prefix for the same reason as the backlog. */
export const sprintWorkItemsQueryKey = (sprintUuid: string) =>
  ['tasks', 'sprint', sprintUuid] as const;

const sprintWorkItemsQuery = (projectUuid: string, sprintUuid: string) => ({
  queryKey: sprintWorkItemsQueryKey(sprintUuid),
  queryFn: () => getSprintWorkItems(projectUuid, sprintUuid),
  enabled: !!projectUuid && !!sprintUuid,
});

export const useSprintWorkItemsQuery = (projectUuid: string, sprintUuid: string) =>
  useQuery(sprintWorkItemsQuery(projectUuid, sprintUuid));

/** Several sprints' work items, keyed by sprint uuid; shares the cache with the query above. */
export const useSprintsWorkItemsQueries = (projectUuid: string, sprintUuids: string[]) =>
  useQueries({
    queries: sprintUuids.map((sprintUuid) => sprintWorkItemsQuery(projectUuid, sprintUuid)),
    combine: (results) =>
      Object.fromEntries(sprintUuids.map((uuid, i) => [uuid, results[i]?.data ?? []])) as Record<
        string,
        Task[]
      >,
  });

/** Under 'tasks' too: the report shows each item's current status and sprint. */
export const sprintReportQueryKey = (sprintUuid: string) =>
  ['tasks', 'sprint-report', sprintUuid] as const;

export const useSprintReportQuery = (sprintUuid?: string) =>
  useQuery({
    queryKey: sprintReportQueryKey(sprintUuid ?? ''),
    queryFn: () => getSprintReport(sprintUuid as string),
    enabled: !!sprintUuid,
  });

/** Under 'tasks' too: reassigning, re-estimating or moving an item changes it. */
export const sprintWorkloadQueryKey = (sprintUuid: string) =>
  ['tasks', 'sprint-workload', sprintUuid] as const;

export const useSprintWorkloadQuery = (sprintUuid?: string) =>
  useQuery({
    queryKey: sprintWorkloadQueryKey(sprintUuid ?? ''),
    queryFn: () => getSprintWorkload(sprintUuid as string),
    enabled: !!sprintUuid,
  });
